import { Link } from 'react-router-dom';
import { Bell, CheckCircle2, X, XCircle } from 'lucide-react';
import { useMe } from '@/lib/hooks';
import { dismissToast, pushToast, useToasts, type Toast } from '@/lib/toasts';
import { useLiveSocket } from '@/lib/ws';
import { cn } from '@/lib/utils';

interface BotEvent {
  type: string;
  timestamp: string;
  [k: string]: unknown;
}

/**
 * Global toast stack, bottom right — mounted once in the App shell so it shows
 * on every page: app toasts (lib/toasts.ts, e.g. "Klonen fertig ✓") plus the
 * readable bot events. Background events (stats, voice / music state, backup
 * progress) don't pop up; results of backup runs come as run toasts
 * (lib/runs.ts), other action results only for the person who started them.
 */
export function LiveEventsToast() {
  const toasts = useToasts();
  const me = useMe();
  const myId = me.data?.user.discordId;

  useLiveSocket((msg) => {
    if (msg.channel !== 'bot:event') return;
    const ev = msg.payload as BotEvent;
    const toast = toastForEvent(ev, myId);
    if (toast) pushToast(toast, 6000);
  });

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastCard({ toast: t }: { toast: Toast }) {
  const Icon = t.tone === 'success' ? CheckCircle2 : t.tone === 'error' ? XCircle : Bell;
  const content = (
    <>
      <div className="mb-1 flex items-center gap-2 pr-5 text-sm font-medium">
        <Icon
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            t.tone === 'success' ? 'text-success' : t.tone === 'error' ? 'text-destructive' : 'text-primary'
          )}
        />
        {t.title}
      </div>
      {t.body && <div className="text-xs text-muted-foreground">{t.body}</div>}
    </>
  );
  return (
    <div className="glass-float pointer-events-auto relative rounded-md border bg-card p-3 shadow-lg">
      {t.to ? (
        <Link to={t.to} onClick={() => dismissToast(t.id)} className="block hover:opacity-90">
          {content}
        </Link>
      ) : (
        content
      )}
      <button
        type="button"
        onClick={() => dismissToast(t.id)}
        className="absolute right-2 top-2 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label="Schließen"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function toastForEvent(ev: BotEvent, myId: string | undefined): Omit<Toast, 'id'> | null {
  switch (ev.type) {
    case 'ready':
      return { title: `Bot online: ${String(ev.username ?? 'Bot')} auf ${String(ev.guildCount ?? '?')} Server` };
    case 'ticket-open':
      return {
        title: `Neues Ticket in ${String(ev.guildName ?? ev.guildId)}`,
        body: `von ${String(ev.username ?? ev.userId)} — ${String(ev.category ?? '')}`,
      };
    case 'protection-incident':
      return {
        title: ev.kind === 'raid' ? '🛡️ Anti-Raid hat eingegriffen' : '🛡️ Anti-Nuke hat eingegriffen',
        body: `${String(ev.guildName ?? ev.guildId)}: ${String(ev.summary ?? '')}`,
        tone: 'error',
        to: ev.guildId ? `/server/${String(ev.guildId)}/protection` : undefined,
      };
    case 'action-result':
      if (ev.runId || !myId || ev.requestedBy !== myId) return null;
      return ev.success
        ? { title: 'Aktion ausgeführt ✓', body: String(ev.action ?? ''), tone: 'success' }
        : { title: 'Aktion fehlgeschlagen', body: `${String(ev.action ?? '')}: ${String(ev.error ?? 'unbekannter Fehler')}`, tone: 'error' };
    default:
      return null;
  }
}
